/**
 * Full-viewport drag-to-select layer for the CoPaya screenshot flow.
 *
 * Rendered through a portal so the fixed AppBar's stacking context cannot
 * clip it, and stacked above every MUI layer (drawer 1200, modal 1300,
 * snackbar 1400, tooltip 1500). It never appears in the capture itself: the
 * orchestrator unmounts it before rasterizing, and `SCREENSHOT_EXCLUDE_ATTR`
 * guards against any straggler frame on top of that.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import type { SelectionRect } from '@/utils/screenshot';

/** Drags smaller than this (CSS px) are treated as stray clicks, not selections. */
const MIN_SELECTION_PX = 8;

interface Point {
  x: number;
  y: number;
}

function pointsToRect(start: Point, current: Point): SelectionRect {
  return {
    x: Math.min(start.x, current.x),
    y: Math.min(start.y, current.y),
    width: Math.abs(current.x - start.x),
    height: Math.abs(current.y - start.y),
  };
}

interface ScreenshotSelectionOverlayProps {
  /** Called with the selected region in viewport CSS px. */
  onSelect: (rect: SelectionRect) => void;
  onCancel: () => void;
}

export function ScreenshotSelectionOverlay({
  onSelect,
  onCancel,
}: ScreenshotSelectionOverlayProps) {
  const { t } = useTranslation('copaya');
  const { t: tc } = useTranslation('common');
  const containerRef = useFocusTrap<HTMLDivElement>(true);
  // The drag origin lives in a ref: pointer events must not depend on the
  // last pointermove having committed a render, and state-updater side
  // effects (StrictMode double-invokes updaters) stay impossible. The rect
  // state below is purely for painting.
  const dragStartRef = useRef<Point | null>(null);
  const [rect, setRect] = useState<SelectionRect | null>(null);
  // The latest cancel callback, so the window listener below never goes stale.
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    // Capture phase + stopPropagation: while the overlay is up, Esc belongs
    // to it alone — nothing else on the page may also react.
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onCancelRef.current();
    };
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, []);

  const clampToViewport = (clientX: number, clientY: number) => ({
    x: Math.max(0, Math.min(clientX, window.innerWidth)),
    y: Math.max(0, Math.min(clientY, window.innerHeight)),
  });

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const start = { x: e.clientX, y: e.clientY };
    dragStartRef.current = start;
    setRect(pointsToRect(start, start));
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStartRef.current;
    if (!start) return;
    setRect(pointsToRect(start, clampToViewport(e.clientX, e.clientY)));
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = dragStartRef.current;
      if (!start) return;
      dragStartRef.current = null;
      e.currentTarget.releasePointerCapture(e.pointerId);
      const finalRect = pointsToRect(start, clampToViewport(e.clientX, e.clientY));
      // A stray click resets to the pre-drag state instead of cancelling.
      setRect(null);
      if (finalRect.width >= MIN_SELECTION_PX && finalRect.height >= MIN_SELECTION_PX) {
        onSelect(finalRect);
      }
    },
    [onSelect],
  );

  const handlePointerCancel = useCallback(() => {
    dragStartRef.current = null;
    setRect(null);
  }, []);

  const overlay = (
    <Box
      ref={containerRef}
      // Literal form of SCREENSHOT_EXCLUDE_ATTR — the capture filter matches on it.
      data-screenshot-exclude=""
      role="dialog"
      aria-modal="true"
      aria-label={t('selectionHint')}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      sx={(theme) => ({
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.tooltip + 1,
        cursor: 'crosshair',
        touchAction: 'none',
        overflow: 'hidden',
        userSelect: 'none',
        backgroundColor: rect ? 'transparent' : alpha(theme.palette.common.black, 0.4),
      })}
    >
      {rect && (
        <Box
          sx={(theme) => ({
            position: 'absolute',
            left: rect.x,
            top: rect.y,
            width: rect.width,
            height: rect.height,
            border: '1px dashed',
            borderColor: 'primary.light',
            // The dim lives on this shadow so the selected region stays a
            // true window onto the page; the root clips the overflow.
            boxShadow: `0 0 0 200vmax ${alpha(theme.palette.common.black, 0.4)}`,
            pointerEvents: 'none',
          })}
        />
      )}
      {!rect && (
        <Paper
          elevation={4}
          onPointerDown={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            top: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1,
            borderRadius: 3,
            cursor: 'default',
          }}
        >
          <Typography variant="body2">{t('selectionHint')}</Typography>
          <Button size="small" onClick={onCancel}>
            {tc('cancel')}
          </Button>
        </Paper>
      )}
    </Box>
  );

  return createPortal(overlay, document.body);
}
